import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useHistory } from "react-router";
import H from "history";
import {
  IResult,
  RecentExperienceSearch,
  LocationQueryEnum,
  ActivitiesEntryTypeEnum,
  ExperienceTag,
  CombinedSearchResultType,
  CombinedSearchGroupTrackingType,
} from "redmond";
import {
  BannerSeverity,
  Icon,
  IconName,
  NotificationBanner,
  B2BSearchAutocomplete,
} from "halifax";
import { isEqual } from "lodash-es";
import { Box, Paper } from "@material-ui/core";
import {
  getExperiencesAvailabilityQuery,
  getExperiencesShopQuery,
  useIsTravelApp,
  useLazyGetExperiencesDestinationQuery,
} from "@capone/common";
import {
  getSelectionName,
  getSelectionId,
} from "../../../../utils/selectionHelpers";

import * as textConstants from "./textConstants";
import { ExperiencesSearchControlConnectorProps } from "./container";

import "./styles.scss";
import { ExperiencesSearchButton } from "./components/SearchButton";

import { SEARCH_EXPERIENCES_BUTTON } from "../../textConstants";
import { CalendarPickerButton } from "./components/CalendarPickerButton";
import { PATH_AVAILABILITY, PATH_SHOP } from "../../../../utils/paths";
import {
  AVAILABLE,
  DATELESS_SEARCH_EXPERIMENT,
  COMBINED_SEARCH_EXPERIMENT,
  COMBINED_SEARCH_IMAGES_EXPERIMENT,
  COMBINED_SEARCH_IMAGES_VARIANTS,
  EXPERIENCES_SEARCH_NEARBY_EXPERIMENT,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { TripDestinationsSection } from "../TripDestinationsSection";
import { CombinedSearchDropdown } from "../CombinedSearchDropdown/component";
import {
  COMBINED_GROUP_TO_ENTRY_TYPE,
  CombinedSearchGroupType,
  CombinedSearchSuggestion,
  TripDestination,
} from "../../types";
import { useAutocompleteAnalytics } from "../../../../hooks/useAutocompleteAnalytics";
import { useCombinedSearchAnalytics } from "../../../../hooks/useCombinedSearchAnalytics";

export interface IExperiencesSearchControlProps
  extends ExperiencesSearchControlConnectorProps {
  className?: string;
  recentSearches?: RecentExperienceSearch[];
  onRecentSearchClick?: (search: RecentExperienceSearch) => void;
  displaySearchOnChangeOnly?: boolean;
  updateParamOnSearch?: boolean;
  hideTripDestinations?: boolean;
  /** Context for combined search scoping — "landing" hides categories, "availability" passes destinationId */
  combinedSearchPageContext?: "landing" | "availability" | "shop";
  /** Destination ID for scoped combined search — auto-derived from Redux location if not provided */
  combinedSearchDestinationId?: string;
  /** Initial text to display in the combined search field (e.g. "Food & Drink - Rome") */
  combinedSearchInitialText?: string;
  /** When true, only renders the search field without date picker or search button */
  combinedSearchOnly?: boolean;
  /** Location label for resolving destination ID when Redux location is unavailable (e.g., shop page) */
  combinedSearchLocationLabel?: string;
  /** Active category/attraction tag — used to initialize selectedCombinedSuggestion on non-landing pages */
  combinedSearchSelectedTag?: ExperienceTag;
}

const getSelectedOption = (option: IResult | null, value: IResult | null) => {
  if (!value?.id || !option?.id) return false;

  try {
    const selectionId = getSelectionId(value.id);
    const optId = getSelectionId(option.id);
    return selectionId === optId;
  } catch {
    return false;
  }
};

export const ExperiencesSearchControl = (
  props: IExperiencesSearchControlProps
) => {
  const {
    className,
    location,
    fromDate,
    untilDate,
    displaySearchOnChangeOnly = false,
    updateParamOnSearch,
    resetFilterAndSort,
    setFromDate,
    setUntilDate,
    setLocation,
    fetchInitialExperiencesAvailability,
    locationCategories,
    fetchLocationCategories,
    recentSearches,
    onRecentSearchClick,
    tripDestinations,
    tripDestinationsLoading,
    hideTripDestinations,
    combinedSearchPageContext = "landing",
    combinedSearchDestinationId,
    combinedSearchInitialText,
    combinedSearchOnly = false,
    combinedSearchLocationLabel,
    combinedSearchSelectedTag,
    // Combined search props
    combinedGroups,
    combinedLoading,
    hasCombinedError,
    fetchCombinedAutocomplete,
    clearCombinedAutocomplete,
    entryTypesAll,
    setEntryType,
  } = props;

  const history = useHistory();

  // Resolve destination ID for combined search scoping.
  // Uses a ref so the value is immediately available in callbacks without stale closures.
  // On the shop page, Redux `location` is owned by the availability saga and may be
  // stale relative to the current shop's destination (e.g., after navigating via
  // "Similar Activities" to a different city). Defer to the saga's locationLabel
  // resolution via `combinedSearchLocationLabel` instead.
  const resolvedDestinationIdRef = useRef<string | undefined>(
    combinedSearchDestinationId ||
      (() => {
        if (combinedSearchPageContext === "shop") return undefined;
        if (combinedSearchPageContext !== "landing" && location?.id) {
          try {
            return getSelectionId(location.id);
          } catch {
            return undefined;
          }
        }
        return undefined;
      })()
  );

  // Keep ref in sync when location or explicit prop changes
  useEffect(() => {
    if (combinedSearchDestinationId) {
      resolvedDestinationIdRef.current = combinedSearchDestinationId;
    } else if (combinedSearchPageContext === "shop") {
      // Stay undefined so the saga resolves via combinedSearchLocationLabel.
      resolvedDestinationIdRef.current = undefined;
    } else if (combinedSearchPageContext !== "landing" && location?.id) {
      try {
        resolvedDestinationIdRef.current = getSelectionId(location.id);
      } catch {
        /* ignore */
      }
    }
  }, [combinedSearchDestinationId, combinedSearchPageContext, location?.id]);

  const [hasMissingSearchInfoError, setHasMissingSearchInfoError] =
    useState(false);
  const [curLocation, setCurLocation] = useState(location);
  const [curFromDate, setCurFromDate] = useState(fromDate);
  const [curUntilDate, setCurUntilDate] = useState(untilDate);
  const [fetchExpDestinations, { data: destinationData }] =
    useLazyGetExperiencesDestinationQuery();

  // On non-landing pages without a Redux location (e.g., shop in new tab),
  // the first combined autocomplete search will be unscoped. The saga's
  // follow-up call pattern handles this by detecting a single destination
  // match and fetching scoped categories. Subsequent searches will use
  // the destination ID extracted from the first response via the saga's
  // setLocation dispatch.

  // Track when a selection just occurred to prevent immediate search on the same Enter key
  const justSelectedRef = useRef(false);
  // Track blur timeout for cleanup
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [autoSearchTrigger, setAutoSearchTrigger] = useState(false);

  const { isTravelApp } = useIsTravelApp();
  // Cleanup blur timeout on unmount
  useEffect(
    () => () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    },
    []
  );

  const expState = useExperiments();
  const isSearchNearbyExperiment =
    getExperimentVariant(
      expState.experiments,
      EXPERIENCES_SEARCH_NEARBY_EXPERIMENT
    ) === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isDatelessSearchEnabled =
    getExperimentVariant(expState.experiments, DATELESS_SEARCH_EXPERIMENT) ===
    AVAILABLE;

  const isCombinedSearchEnabled =
    getExperimentVariant(expState.experiments, COMBINED_SEARCH_EXPERIMENT) ===
    AVAILABLE;

  const combinedSearchImageVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    COMBINED_SEARCH_IMAGES_EXPERIMENT,
    COMBINED_SEARCH_IMAGES_VARIANTS
  );

  // Helper to determine if trip suggestions are loaded
  const tripSuggestionsLoaded =
    !tripDestinationsLoading && tripDestinations.length > 0;

  const searchNearbyLocationResult = destinationData?.categories[0].results[0];

  // Build a CombinedSearchSuggestion from existing Redux state (location + optional tag)
  // so the search button works without requiring a dropdown selection.
  const buildSuggestionFromState = useCallback((): {
    suggestion: CombinedSearchSuggestion | null;
    groupType: CombinedSearchGroupType | null;
  } => {
    if (combinedSearchPageContext === "landing" || !location?.label) {
      return { suggestion: null, groupType: null };
    }
    let destId: string | undefined;
    try {
      destId = getSelectionId(location.id);
    } catch {
      /* ignore */
    }

    if (combinedSearchSelectedTag) {
      const { kind } = combinedSearchSelectedTag;
      const isAttraction =
        kind &&
        "ActivityTagKind" in kind &&
        kind.ActivityTagKind === "Attraction";
      return {
        suggestion: {
          id: String(combinedSearchSelectedTag.id),
          label: combinedSearchSelectedTag.name,
          suggestionType: isAttraction ? "Attraction" : "Category",
          destinationId: destId,
          destinationName: location.label,
        },
        groupType: isAttraction ? "Attraction" : "Category",
      };
    }
    return {
      suggestion: {
        id: destId || "",
        label: location.label,
        suggestionType: "Destination",
        destinationId: destId,
        destinationName: location.label,
      },
      groupType: "Destination",
    };
  }, [
    combinedSearchPageContext,
    location?.id,
    location?.label,
    combinedSearchSelectedTag,
  ]);

  const initialSuggestion = useMemo(
    () => buildSuggestionFromState(),
    [buildSuggestionFromState]
  );

  // Combined search state — on landing page start blank (fresh search context);
  // on other pages pre-fill from initialText or current location
  const [combinedQuery, setCombinedQuery] = useState(
    combinedSearchPageContext === "landing"
      ? ""
      : combinedSearchInitialText || location?.label || ""
  );
  const [isCombinedDropdownOpen, setIsCombinedDropdownOpen] = useState(false);
  const [combinedHasSearched, setCombinedHasSearched] = useState(false);
  const [selectedCombinedSuggestion, setSelectedCombinedSuggestion] =
    useState<CombinedSearchSuggestion | null>(initialSuggestion.suggestion);
  const [selectedCombinedGroupType, setSelectedCombinedGroupType] =
    useState<CombinedSearchGroupType | null>(initialSuggestion.groupType);

  // Sync combined search state when location or tag changes (e.g. category pill click).
  // On landing page, don't pre-fill from Redux location — it's stale from prior searches.
  useEffect(() => {
    if (!isCombinedSearchEnabled) return;
    if (combinedSearchInitialText) {
      setCombinedQuery(combinedSearchInitialText);
    } else if (combinedSearchPageContext !== "landing" && location?.label) {
      setCombinedQuery(location.label);
    }
    const derived = buildSuggestionFromState();
    setSelectedCombinedSuggestion(derived.suggestion);
    setSelectedCombinedGroupType(derived.groupType);
  }, [
    isCombinedSearchEnabled,
    combinedSearchInitialText,
    combinedSearchPageContext,
    location?.label,
    buildSuggestionFromState,
  ]);

  const {
    resetRefs: resetCombinedAnalyticsRefs,
    trackFocused: trackCombinedFocused,
    trackTyped: trackCombinedTyped,
    trackResultClicked: trackCombinedResultClicked,
    trackRecentClicked: trackCombinedRecentClicked,
    trackCleared: trackCombinedCleared,
    trackNoResults: trackCombinedNoResults,
  } = useCombinedSearchAnalytics({
    pageContext: combinedSearchPageContext,
    hasRecentSearches: (recentSearches?.length ?? 0) > 0,
  });

  const {
    resetAnalyticsRefs,
    trackAutocompleteFocus,
    trackAutocompleteTyped,
    trackTripClick,
    trackRecentSearchClick,
    trackLocationSelected,
    trackSearchNearbyClick,
  } = useAutocompleteAnalytics({
    tripSuggestionsLoaded,
    tripDestinationsCount: tripDestinations.length,
    locationCategories,
    hasNearbyResult: !!searchNearbyLocationResult,
    isSearchNearbyExperiment,
  });

  const searchNearby = useCallback((latitude: number, longitude: number) => {
    fetchExpDestinations({
      query: {
        LocationQuery: LocationQueryEnum.Coordinate,
        lat: latitude,
        lon: longitude,
      },
    });
  }, []);

  const handleSearchNearbyClick = useCallback(() => {
    if (searchNearbyLocationResult) {
      trackSearchNearbyClick();
      setLocation(searchNearbyLocationResult);
    }
  }, [searchNearbyLocationResult, setLocation, trackSearchNearbyClick]);

  const handleRecentSearchClick = useCallback(
    (search: RecentExperienceSearch) => {
      const searchIndex = recentSearches?.indexOf(search) ?? 0;
      trackRecentSearchClick(searchIndex);
      onRecentSearchClick?.(search);
    },
    [onRecentSearchClick, recentSearches, trackRecentSearchClick]
  );

  // Track focus event for autocomplete
  const handleAutocompleteFocus = useCallback(() => {
    // Clear any pending blur timeout to prevent race condition
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    trackAutocompleteFocus();
  }, [trackAutocompleteFocus]);

  // Track when autocomplete loses focus (reset the fired flags)
  const handleAutocompleteBlur = useCallback(() => {
    // Clear any existing timeout to prevent race conditions
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    // Use a timeout to avoid resetting too early if user is clicking within autocomplete
    blurTimeoutRef.current = setTimeout(() => {
      resetAnalyticsRefs();
      blurTimeoutRef.current = null;
    }, 200);
  }, [resetAnalyticsRefs]);

  // Wrap fetchLocationCategories to track first keystroke
  const handleFetchLocationCategories = useCallback(
    (queryString: string) => {
      if (queryString.length > 0) {
        trackAutocompleteTyped();
      }
      fetchLocationCategories(queryString);
    },
    [fetchLocationCategories, trackAutocompleteTyped]
  );

  useEffect(() => {
    setCurLocation(location);
  }, [location]);

  useEffect(() => {
    setCurFromDate(fromDate);
  }, [fromDate]);

  useEffect(() => {
    setCurUntilDate(untilDate);
  }, [untilDate]);

  const isReadyToSearch = useMemo(() => {
    if (isCombinedSearchEnabled) {
      if (!selectedCombinedSuggestion) return false;
      if (selectedCombinedGroupType === "Product") return true;
      return isDatelessSearchEnabled || (!!curFromDate && !!curUntilDate);
    }
    return isDatelessSearchEnabled
      ? !!curLocation
      : !!curLocation && !!curFromDate && !!curUntilDate;
  }, [
    isCombinedSearchEnabled,
    selectedCombinedSuggestion,
    selectedCombinedGroupType,
    curLocation,
    curFromDate,
    curUntilDate,
    isDatelessSearchEnabled,
  ]);

  useEffect(() => {
    if (isReadyToSearch) {
      setHasMissingSearchInfoError(false);
    }
  }, [isReadyToSearch]);

  const handleSearchClick = useCallback(
    // eslint-disable-next-line no-shadow -- parameter matches outer useHistory for API consistency
    (history: H.History) => {
      if (!isReadyToSearch) {
        setHasMissingSearchInfoError(true);
        return;
      }

      // Combined search routing based on selected suggestion type
      if (
        isCombinedSearchEnabled &&
        selectedCombinedSuggestion &&
        selectedCombinedGroupType
      ) {
        const suggestion = selectedCombinedSuggestion;
        const groupType = selectedCombinedGroupType;
        let didNavigate = false;

        const entryTypeForGroup = COMBINED_GROUP_TO_ENTRY_TYPE[groupType];
        if (entryTypeForGroup != null) {
          setEntryType(entryTypeForGroup);
        }

        switch (groupType) {
          case "Destination":
            history.push(
              `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
                suggestion.label,
                curFromDate,
                curUntilDate,
                undefined,
                isTravelApp,
                ActivitiesEntryTypeEnum.COMBINED_SEARCH_DESTINATION,
                undefined,
                undefined,
                entryTypesAll ?? []
              )}`
            );
            didNavigate = true;
            break;
          case "Category":
          case "Attraction": {
            const destName = suggestion.destinationName || suggestion.label;
            const entryType =
              groupType === "Category"
                ? ActivitiesEntryTypeEnum.COMBINED_SEARCH_CATEGORY
                : ActivitiesEntryTypeEnum.COMBINED_SEARCH_ATTRACTION;
            // Both use categoryTagId param — suffix _0 for Category, _1 for Attraction
            const kindSuffix = groupType === "Category" ? "_0" : "_1";
            const filterParam = `&categoryTagId=${suggestion.id}${kindSuffix}`;
            history.push(
              `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
                destName,
                curFromDate,
                curUntilDate,
                undefined,
                isTravelApp,
                entryType,
                undefined,
                undefined,
                entryTypesAll ?? []
              )}${filterParam}`
            );
            didNavigate = true;
            break;
          }
          case "Product": {
            const shopFromDate = curFromDate || dayjs().toDate();
            const shopUntilDate =
              curUntilDate || dayjs().add(7, "day").toDate();
            window.open(
              `${PATH_SHOP}${getExperiencesShopQuery(
                { value: suggestion.id },
                shopFromDate,
                shopUntilDate,
                suggestion.destinationName,
                isTravelApp,
                undefined,
                ActivitiesEntryTypeEnum.COMBINED_SEARCH_PRODUCT,
                entryTypesAll ?? []
              )}`,
              "_blank"
            );
            didNavigate = true;
            break;
          }
          default:
            break;
        }

        if (didNavigate && updateParamOnSearch) {
          resetFilterAndSort();
          setFromDate(curFromDate);
          setUntilDate(curUntilDate);
          fetchInitialExperiencesAvailability(history, isDatelessSearchEnabled);
        }
        return;
      }

      // Existing location-based search
      history.push(
        `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
          curLocation?.id ? getSelectionName(curLocation.id) : "",
          curFromDate,
          curUntilDate,
          undefined,
          isTravelApp,
          ActivitiesEntryTypeEnum.HOMEPAGE
        )}`
      );

      if (updateParamOnSearch) {
        resetFilterAndSort();
        setFromDate(curFromDate);
        setUntilDate(curUntilDate);
        setLocation(curLocation);
        fetchInitialExperiencesAvailability(history, isDatelessSearchEnabled);
      }
    },
    [
      isReadyToSearch,
      isCombinedSearchEnabled,
      selectedCombinedSuggestion,
      selectedCombinedGroupType,
      curFromDate,
      curUntilDate,
      curLocation,
      isTravelApp,
      entryTypesAll,
      setEntryType,
    ]
  );

  const handleCalendarClose = useCallback(() => {
    if (!isDatelessSearchEnabled || !displaySearchOnChangeOnly) return;
    setAutoSearchTrigger(true);
  }, [isDatelessSearchEnabled, displaySearchOnChangeOnly]);

  useEffect(() => {
    if (autoSearchTrigger && curFromDate && curUntilDate) {
      setAutoSearchTrigger(false);
      handleSearchClick(history);
    }
  }, [
    autoSearchTrigger,
    curFromDate,
    curUntilDate,
    handleSearchClick,
    history,
  ]);

  const hasChanges = useMemo(() => {
    if (isCombinedSearchEnabled) {
      // For combined search, only a new suggestion selection or date change counts
      return (
        !!selectedCombinedSuggestion ||
        !isEqual(curFromDate, fromDate) ||
        !isEqual(curUntilDate, untilDate)
      );
    }
    return (
      !isEqual(curLocation, location) ||
      !isEqual(curFromDate, fromDate) ||
      !isEqual(curUntilDate, untilDate)
    );
  }, [
    isCombinedSearchEnabled,
    selectedCombinedSuggestion,
    curLocation,
    location,
    curFromDate,
    fromDate,
    curUntilDate,
    untilDate,
  ]);

  const handleTripClick = useCallback(
    (trip: TripDestination, index: number) => {
      trackTripClick(index);
      setEntryType(ActivitiesEntryTypeEnum.UPCOMING_TRIP);
      history.push(
        `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
          trip.location.id ? getSelectionName(trip.location.id) : "",
          dayjs(trip.tripDate).toDate(),
          dayjs(trip.tripEndDate).toDate(),
          undefined,
          isTravelApp,
          ActivitiesEntryTypeEnum.UPCOMING_TRIP,
          undefined,
          undefined,
          entryTypesAll ?? []
        )}`
      );
    },
    [history, trackTripClick, setEntryType, entryTypesAll]
  );

  // Wrapper to track when a location is selected from the autocomplete
  const handleLocationChange = useCallback(
    (newLocation: IResult | null) => {
      if (newLocation) {
        trackLocationSelected(newLocation.id);
      }

      justSelectedRef.current = true;
      setCurLocation(newLocation);
      // Reset the flag after a short delay to allow subsequent Enter presses to trigger search
      setTimeout(() => {
        justSelectedRef.current = false;
      }, 100);
    },
    [trackLocationSelected]
  );

  const handleSearchInputKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // When Enter is pressed with a location already selected, trigger the search
      // Skip if we just selected a value (the same Enter key that selected the option)
      if (event.key === "Enter" && curLocation && !justSelectedRef.current) {
        handleSearchClick(history);
      }
    },
    [curLocation, handleSearchClick, history]
  );

  // Get the destination ID for scoping, reading from the ref which is kept
  // in sync by the effects above (from Redux location or explicit prop).
  const getDestinationId = useCallback(
    (): string | undefined => resolvedDestinationIdRef.current,
    []
  );

  // Combined search handlers
  const handleCombinedInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setCombinedQuery(value);
      setCombinedHasSearched(true);
      // Clear any previous selection when user modifies the text
      setSelectedCombinedSuggestion(null);
      setSelectedCombinedGroupType(null);
      if (value.trim().length > 0) {
        const destId = getDestinationId();
        fetchCombinedAutocomplete({
          query: value,
          destinationId: destId,
          // Always send the location label on non-landing pages so the saga
          // can both (a) resolve a destinationId when none is set and
          // (b) enforce destination-name filtering on the response when one
          // is set. Without this, the FE has no way to drop suggestions
          // tagged with a different destination when the BE's destinationId
          // scoping is incomplete.
          locationLabel:
            combinedSearchPageContext !== "landing"
              ? combinedSearchLocationLabel || location?.label
              : undefined,
        });
      } else {
        clearCombinedAutocomplete();
        trackCombinedCleared();
      }
    },
    [
      fetchCombinedAutocomplete,
      clearCombinedAutocomplete,
      trackCombinedCleared,
      getDestinationId,
      combinedSearchPageContext,
      combinedSearchLocationLabel,
      location?.label,
    ]
  );

  // Fire typed/no_results events when the API response arrives (not on keystroke,
  // so we capture the full query rather than just the first letter)
  const prevCombinedLoadingRef = useRef(combinedLoading);
  useEffect(() => {
    if (
      prevCombinedLoadingRef.current &&
      !combinedLoading &&
      combinedQuery.trim().length > 0
    ) {
      trackCombinedTyped(combinedQuery);
      if (
        !hasCombinedError &&
        combinedGroups.every((g) => g.suggestions.length === 0)
      ) {
        trackCombinedNoResults(combinedQuery);
      }
    }
    prevCombinedLoadingRef.current = combinedLoading;
  }, [
    combinedLoading,
    combinedGroups,
    hasCombinedError,
    combinedQuery,
    trackCombinedTyped,
    trackCombinedNoResults,
  ]);

  const combinedInputRef = useRef<HTMLInputElement>(null);

  const handleCombinedFocus = useCallback(() => {
    // Select all text on focus so user can type over or backspace to clear
    setTimeout(() => {
      combinedInputRef.current?.select();
    }, 0);
    setCombinedHasSearched(false);
    clearCombinedAutocomplete();
    setIsCombinedDropdownOpen(true);
    trackCombinedFocused();
  }, [trackCombinedFocused, clearCombinedAutocomplete]);

  const handleCombinedBlur = useCallback(() => {
    // Delay to allow click events in dropdown
    setTimeout(() => {
      setIsCombinedDropdownOpen(false);
      resetCombinedAnalyticsRefs();
    }, 200);
  }, [resetCombinedAnalyticsRefs]);

  const handleCombinedResultClick = useCallback(
    (
      suggestion: CombinedSearchSuggestion,
      groupType: CombinedSearchGroupType,
      index: number
    ) => {
      setIsCombinedDropdownOpen(false);

      // Store enrichment data for the availability/shop tracking to pick up
      try {
        localStorage.setItem(
          "combined_search_enrichment",
          JSON.stringify({
            label: suggestion.label,
            thumbnailUrl: suggestion.thumbnailUrl,
            activityCount: suggestion.activityCount,
            suggestionType: suggestion.suggestionType || groupType,
          })
        );
      } catch {
        // Ignore localStorage errors
      }

      const resultTypeMap: Record<
        CombinedSearchGroupType,
        CombinedSearchResultType
      > = {
        Destination: CombinedSearchResultType.Destination,
        Category: CombinedSearchResultType.Category,
        Attraction: CombinedSearchResultType.Attraction,
        Product: CombinedSearchResultType.Product,
        Collection: CombinedSearchResultType.Collection,
      };
      const groupTrackingMap: Record<
        CombinedSearchGroupType,
        CombinedSearchGroupTrackingType
      > = {
        Destination: "Destination",
        Category: "Categories",
        Attraction: "Attraction",
        Product: "Products",
        Collection: "Collection",
      };

      trackCombinedResultClicked({
        resultType: resultTypeMap[groupType],
        resultIndex: index,
        groupType: groupTrackingMap[groupType],
        searchQuery: combinedQuery,
        destinationId: suggestion.destinationId,
        destinationName: suggestion.destinationName,
      });

      const entryTypeForResult = COMBINED_GROUP_TO_ENTRY_TYPE[groupType];
      if (entryTypeForResult != null) {
        setEntryType(entryTypeForResult);
      }

      // On availability/shop page with dates: navigate immediately
      const hasDates = !!(curFromDate && curUntilDate);
      const shouldNavigateImmediately =
        combinedSearchPageContext !== "landing" &&
        (hasDates || isDatelessSearchEnabled);
      if (shouldNavigateImmediately) {
        let didNavigate = false;
        clearCombinedAutocomplete();

        switch (groupType) {
          case "Destination":
            setCombinedQuery(suggestion.label);
            history.push(
              `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
                suggestion.label,
                curFromDate,
                curUntilDate,
                undefined,
                isTravelApp,
                ActivitiesEntryTypeEnum.COMBINED_SEARCH_DESTINATION,
                undefined,
                undefined,
                entryTypesAll ?? []
              )}`
            );
            didNavigate = true;
            break;
          case "Category":
          case "Attraction": {
            // Use current page location (Redux) first, then suggestion's destination,
            // then the location label prop (from URL on shop page), then the category label as last resort
            const destName =
              location?.label ||
              suggestion.destinationName ||
              combinedSearchLocationLabel ||
              suggestion.label;
            setCombinedQuery(destName);
            const entryType =
              groupType === "Category"
                ? ActivitiesEntryTypeEnum.COMBINED_SEARCH_CATEGORY
                : ActivitiesEntryTypeEnum.COMBINED_SEARCH_ATTRACTION;
            const kindSuffix = groupType === "Category" ? "_0" : "_1";
            const filterParam = `&categoryTagId=${suggestion.id}${kindSuffix}`;
            history.push(
              `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
                destName,
                curFromDate,
                curUntilDate,
                undefined,
                isTravelApp,
                entryType,
                undefined,
                undefined,
                entryTypesAll ?? []
              )}${filterParam}`
            );
            didNavigate = true;
            break;
          }
          case "Product": {
            window.open(
              `${PATH_SHOP}${getExperiencesShopQuery(
                { value: suggestion.id },
                curFromDate,
                curUntilDate,
                suggestion.destinationName,
                isTravelApp,
                undefined,
                ActivitiesEntryTypeEnum.COMBINED_SEARCH_PRODUCT,
                entryTypesAll ?? []
              )}`,
              "_blank"
            );
            didNavigate = true;
            break;
          }
          default:
            break;
        }

        if (didNavigate && updateParamOnSearch) {
          resetFilterAndSort();
          setFromDate(curFromDate);
          setUntilDate(curUntilDate);
          fetchInitialExperiencesAvailability(history, isDatelessSearchEnabled);
        }
        return;
      }

      // Landing page: populate the field, let user pick dates and click search
      setCombinedQuery(suggestion.label);
      setSelectedCombinedSuggestion(suggestion);
      setSelectedCombinedGroupType(groupType);
      clearCombinedAutocomplete();
    },
    [
      combinedQuery,
      trackCombinedResultClicked,
      clearCombinedAutocomplete,
      updateParamOnSearch,
      curFromDate,
      curUntilDate,
      isTravelApp,
      history,
      resetFilterAndSort,
      setFromDate,
      setUntilDate,
      fetchInitialExperiencesAvailability,
      entryTypesAll,
      setEntryType,
    ]
  );

  const handleCombinedRecentSearchClick = useCallback(
    (search: RecentExperienceSearch, index: number) => {
      setIsCombinedDropdownOpen(false);
      trackCombinedRecentClicked(index, combinedQuery);
      onRecentSearchClick?.(search);
    },
    [onRecentSearchClick, combinedQuery, trackCombinedRecentClicked]
  );

  const handleCombinedTripClick = useCallback(
    (trip: TripDestination, index: number) => {
      setIsCombinedDropdownOpen(false);
      trackCombinedResultClicked({
        resultType: CombinedSearchResultType.Trip,
        resultIndex: index,
        groupType: "Trip",
        searchQuery: combinedQuery,
        destinationName: trip.displayName,
      });
      setEntryType(ActivitiesEntryTypeEnum.UPCOMING_TRIP);
      history.push(
        `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
          trip.displayName,
          dayjs(trip.tripDate).toDate(),
          dayjs(trip.tripEndDate).toDate(),
          undefined,
          isTravelApp,
          ActivitiesEntryTypeEnum.UPCOMING_TRIP,
          undefined,
          undefined,
          entryTypesAll ?? []
        )}`
      );
    },
    [
      history,
      combinedQuery,
      isTravelApp,
      trackCombinedResultClicked,
      setEntryType,
      entryTypesAll,
    ]
  );

  // Filter combined groups based on page context:
  // - Landing page: exclude Category groups (keep Attraction under "Categories" heading, Destination, Product)
  // - Availability/Shop: show all groups (categories are useful with a destination context)
  const filteredCombinedGroups = useMemo(() => {
    if (combinedSearchPageContext === "landing") {
      return combinedGroups.filter((g) => g.groupType !== "Category");
    }
    return combinedGroups;
  }, [combinedGroups, combinedSearchPageContext]);

  const shouldShowTripDestinations = !hideTripDestinations;

  const recentlySearchedTopContent = useMemo(() => {
    const hasTripContent =
      shouldShowTripDestinations &&
      (tripDestinationsLoading || tripDestinations.length > 0);
    const hasRecentSearches =
      recentSearches && recentSearches.length > 0 && onRecentSearchClick;

    if (!hasTripContent && !hasRecentSearches) {
      return undefined;
    }

    return (
      <TripDestinationsSection
        tripDestinations={shouldShowTripDestinations ? tripDestinations : []}
        loading={shouldShowTripDestinations ? tripDestinationsLoading : false}
        onTripClick={handleTripClick}
        recentSearches={recentSearches}
        onRecentSearchClick={handleRecentSearchClick}
        searchNearby={isSearchNearbyExperiment ? searchNearby : undefined}
        searchNearbyLocation={
          searchNearbyLocationResult?.id
            ? getSelectionName(searchNearbyLocationResult.id)
            : undefined
        }
        handleSearchNearbyClick={handleSearchNearbyClick}
      />
    );
  }, [
    shouldShowTripDestinations,
    tripDestinations,
    tripDestinationsLoading,
    handleTripClick,
    recentSearches,
    handleRecentSearchClick,
    isSearchNearbyExperiment,
    searchNearby,
    searchNearbyLocationResult?.id,
    handleSearchNearbyClick,
  ]);

  return (
    <Box
      className={clsx("experiences-search-control-root", className, {
        "combined-search-only": combinedSearchOnly,
      })}
    >
      <Box className="experiences-search-control-row">
        <Box className="experiences-search-inputs">
          <Box className="experiences-search-input-row">
            <Box
              className="destination-auto-complete-wrapper"
              onKeyDown={
                isCombinedSearchEnabled ? undefined : handleSearchInputKeyDown
              }
              onFocus={
                isCombinedSearchEnabled ? undefined : handleAutocompleteFocus
              }
              onBlur={
                isCombinedSearchEnabled ? undefined : handleAutocompleteBlur
              }
            >
              {isCombinedSearchEnabled ? (
                <div
                  className="combined-search-inline-wrapper"
                  style={{ position: "relative" }}
                >
                  <div
                    className={clsx("combined-search-input-box", {
                      "has-value": combinedQuery.length > 0,
                      "has-error":
                        hasMissingSearchInfoError &&
                        !selectedCombinedSuggestion,
                    })}
                  >
                    <span className="combined-search-icon">
                      <Icon
                        name={IconName.B2BMapPin}
                        ariaLabel=""
                        aria-hidden
                      />
                    </span>
                    <div className="combined-search-input-area">
                      <span className="combined-search-top-label">
                        {combinedQuery.length > 0
                          ? "Search by name or keyword"
                          : ""}
                      </span>
                      <input
                        ref={combinedInputRef}
                        className="combined-search-native-input"
                        value={combinedQuery}
                        onChange={handleCombinedInputChange}
                        onFocus={handleCombinedFocus}
                        onBlur={handleCombinedBlur}
                        placeholder={
                          combinedQuery.length === 0
                            ? "Search by name or keyword"
                            : ""
                        }
                      />
                    </div>
                  </div>
                  {isCombinedDropdownOpen && !selectedCombinedSuggestion && (
                    <Paper
                      className="combined-search-inline-dropdown"
                      elevation={3}
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        minWidth: "100%",
                        width: "max-content",
                        maxWidth: "500px",
                        zIndex: 1300,
                      }}
                    >
                      <CombinedSearchDropdown
                        groups={filteredCombinedGroups}
                        loading={combinedLoading}
                        hasError={hasCombinedError}
                        query={combinedQuery}
                        recentSearches={recentSearches || []}
                        tripDestinations={
                          shouldShowTripDestinations ? tripDestinations : []
                        }
                        showTrips={shouldShowTripDestinations}
                        tripsLoading={
                          shouldShowTripDestinations
                            ? tripDestinationsLoading
                            : false
                        }
                        imageVariant={combinedSearchImageVariant}
                        hasSearched={combinedHasSearched}
                        onResultClick={handleCombinedResultClick}
                        onRecentSearchClick={handleCombinedRecentSearchClick}
                        onTripClick={handleCombinedTripClick}
                      />
                    </Paper>
                  )}
                </div>
              ) : (
                <B2BSearchAutocomplete
                  className="b2b experiences-autocomplete"
                  label={textConstants.LOCATION_AUTOCOMPLETE_PLACEHOLDER}
                  getOptionSelected={getSelectedOption}
                  customIcon={
                    <Icon name={IconName.B2BMapPin} ariaLabel="" aria-hidden />
                  }
                  hasMissingSearchInfoError={
                    hasMissingSearchInfoError && !location
                  }
                  value={curLocation}
                  setValue={handleLocationChange}
                  valueCategories={locationCategories}
                  fetchValueCategories={handleFetchLocationCategories}
                  recentlySearchedTopContent={recentlySearchedTopContent}
                />
              )}
            </Box>
            {!combinedSearchOnly && (
              <Box className="date-input" data-testid="desktop-date-picker">
                <CalendarPickerButton
                  classes={["date-pickers"]}
                  fromDate={curFromDate}
                  untilDate={curUntilDate}
                  setFromDate={setCurFromDate}
                  setUntilDate={setCurUntilDate}
                  hasMissingSearchInfoError={
                    hasMissingSearchInfoError && !fromDate && !untilDate
                  }
                  onClose={handleCalendarClose}
                />
              </Box>
            )}
          </Box>
        </Box>
        {!combinedSearchOnly && (!displaySearchOnChangeOnly || hasChanges) && (
          <ExperiencesSearchButton
            className={clsx("experiences-search-control-button b2b", {
              xmod: isXmodRedesign,
            })}
            message={SEARCH_EXPERIENCES_BUTTON}
            onClick={handleSearchClick}
          />
        )}
      </Box>
      {hasMissingSearchInfoError && (
        <Box className="missing-info-search-error-container">
          <NotificationBanner
            className="missing-info-search-error-banner"
            label={textConstants.MISSING_INFO_SEARCH_ERROR}
            severity={BannerSeverity.ERROR}
            icon={<Icon name={IconName.WarningAlert} />}
          />
        </Box>
      )}
    </Box>
  );
};
